import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const todayString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const dateLabel = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
  weekday: 'long', month: 'short', day: 'numeric'
});

const timeLabel = (date) => date ? new Date(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

const compactConcern = (check) => {
  const fields = [
    ['general_condition', { quiet: 'Quiet / subdued', agitated: 'Agitated', unwell: 'Unwell' }],
    ['appetite', { reduced: 'Reduced appetite', not_eating: 'Not eating' }],
    ['water', { reduced: 'Reduced water', excessive: 'Excessive drinking' }],
    ['manure_urination', { loose: 'Loose manure', dry: 'Dry manure', none_observed: 'No manure observed', urination_concern: 'Urination concern' }],
    ['movement', { stiff: 'Stiff', lame: 'Lame', reluctant: 'Reluctant to move' }],
    ['legs_feet', { heat: 'Heat', swelling: 'Swelling', wound: 'Wound', hoof_concern: 'Hoof concern' }],
  ];
  const parts = [];
  fields.forEach(([key, labels]) => {
    const v = check?.[key];
    if (v && v !== 'normal' && labels[v]) parts.push(labels[v]);
  });
  return parts.slice(0, 2).join(' · ') || 'Concern recorded';
};

const statusLabel = status => ({
  open: 'Concern', improving: 'Improving', no_change: 'No change', worse: 'Worse', resolved: 'Resolved'
}[status] || 'Concern');

const Avatar = ({ horse, size = 42 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {horse.avatar_url ? <img src={horse.avatar_url} alt={horse.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: size * 0.48 }}>🐴</span>}
  </div>
);

const ChoiceRow = ({ label, value, options, onChange }) => {
  const selected = options.find(o => o.value === value);
  const abnormal = value && value !== 'normal';
  return (
    <div className="card" style={{ padding: '11px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ maxWidth: 180, border: 'none', background: 'transparent', color: abnormal ? '#C76822' : 'var(--cr-green-dark)', fontSize: 12, fontWeight: 700, textAlign: 'right', outline: 'none' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ color: abnormal ? '#C76822' : 'var(--cr-green-dark)' }}>›</span>
    </div>
  );
};

const DailyHorseCheckPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ horses: [], completed: 0, normal: 0, concerns: 0, actor: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirmNormal, setConfirmNormal] = useState(false);
  const [concernHorse, setConcernHorse] = useState(null);
  const [followUpCheck, setFollowUpCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [form, setForm] = useState({
    general_condition: 'normal', appetite: 'normal', water: 'normal', manure_urination: 'normal',
    movement: 'normal', legs_feet: 'normal', temperature_f: '', notes: ''
  });
  const [followForm, setFollowForm] = useState({ status: 'improving', notes: '' });
  const date = todayString();
  const period = 'morning';

  const load = async () => {
    try {
      const { data: result } = await api.get('/horse-checks/today', { params: { date, period } });
      setData(result);
      setSelected(prev => {
        const allowed = new Set(result.horses.filter(h => !h.check && h.can_record).map(h => String(h.id)));
        return new Set([...prev].filter(id => allowed.has(id)));
      });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load daily horse checks');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unchecked = useMemo(() => data.horses.filter(h => !h.check && h.can_record), [data.horses]);
  const selectedHorses = useMemo(() => data.horses.filter(h => selected.has(String(h.id))), [data.horses, selected]);
  const activeConcerns = useMemo(() => data.horses.filter(h => h.check?.is_concern && h.check.concern_status !== 'resolved'), [data.horses]);

  const toggleHorse = id => {
    const next = new Set(selected);
    const key = String(id);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(unchecked.map(h => String(h.id))));
  const clear = () => setSelected(new Set());

  const markNormal = async (horseIds = [...selected]) => {
    if (!horseIds.length) return;
    setSaving(true);
    try {
      const { data: result } = await api.post('/horse-checks/bulk-normal', { horse_ids: horseIds, date, period });
      if (result.blocked?.length) toast.error(`${result.blocked.length} horse${result.blocked.length === 1 ? '' : 's'} already had a concern or could not be updated`);
      else toast.success(`${result.saved.length} horse${result.saved.length === 1 ? '' : 's'} marked normal`);
      setSelected(new Set());
      setConfirmNormal(false);
      if (concernHorse) setConcernHorse(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save checks');
    } finally { setSaving(false); }
  };

  const openConcern = horse => {
    setConcernHorse(horse);
    setPhoto(null);
    setForm({
      general_condition: horse.check?.general_condition || 'normal',
      appetite: horse.check?.appetite || 'normal',
      water: horse.check?.water || 'normal',
      manure_urination: horse.check?.manure_urination || 'normal',
      movement: horse.check?.movement || 'normal',
      legs_feet: horse.check?.legs_feet || 'normal',
      temperature_f: horse.check?.temperature_f || '',
      notes: horse.check?.notes || '',
    });
  };

  const saveConcern = async () => {
    if (!concernHorse) return;
    const hasAbnormal = ['general_condition','appetite','water','manure_urination','movement','legs_feet'].some(k => form[k] !== 'normal') || form.temperature_f || form.notes.trim();
    if (!hasAbnormal) return toast.error('Choose a concern, enter a temperature, or add a note');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('date', date);
      fd.append('period', period);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append('photo', photo);
      await api.post(`/horse-checks/${concernHorse.id}/concern`, fd);
      toast.success(`${concernHorse.name} concern recorded`);
      setConcernHorse(null);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save concern');
    } finally { setSaving(false); }
  };

  const saveFollowUp = async () => {
    if (!followUpCheck) return;
    setSaving(true);
    try {
      await api.post(`/horse-checks/${followUpCheck.id}/follow-up`, followForm);
      toast.success('Follow-up saved');
      setFollowUpCheck(null);
      setFollowForm({ status: 'improving', notes: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save follow-up');
    } finally { setSaving(false); }
  };

  const selectedOne = selectedHorses.length === 1 ? selectedHorses[0] : null;

  if (concernHorse) {
    return (
      <div>
        <div style={{ background: 'white', borderBottom: '1px solid var(--border-color)', padding: '14px 16px 10px', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setConcernHorse(null)} style={{ border: 'none', background: 'none', fontSize: 26, cursor: 'pointer' }}>‹</button>
            <div style={{ textAlign: 'center', flex: 1, marginRight: 30 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{concernHorse.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Morning Check</div>
            </div>
          </div>
        </div>
        <div className="page-content" style={{ paddingBottom: 110 }}>
          <button className="btn btn-secondary btn-full" style={{ border: '1px solid var(--cr-green)', color: 'var(--cr-green-dark)', background: 'white', marginBottom: 14 }} onClick={() => markNormal([String(concernHorse.id)])} disabled={saving}>
            ✓ Everything looks normal
          </button>
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, marginBottom: 10 }}>OR RECORD A CONCERN</div>

          <ChoiceRow label="General condition" value={form.general_condition} onChange={v => setForm(p => ({ ...p, general_condition: v }))}
            options={[{value:'normal',label:'Normal'},{value:'quiet',label:'Quiet / subdued'},{value:'agitated',label:'Agitated'},{value:'unwell',label:'Unwell'}]} />
          <ChoiceRow label="Appetite" value={form.appetite} onChange={v => setForm(p => ({ ...p, appetite: v }))}
            options={[{value:'normal',label:'Normal'},{value:'reduced',label:'Reduced'},{value:'not_eating',label:'Not eating'}]} />
          <ChoiceRow label="Water" value={form.water} onChange={v => setForm(p => ({ ...p, water: v }))}
            options={[{value:'normal',label:'Normal'},{value:'reduced',label:'Reduced'},{value:'excessive',label:'Excessive'}]} />
          <ChoiceRow label="Manure / urination" value={form.manure_urination} onChange={v => setForm(p => ({ ...p, manure_urination: v }))}
            options={[{value:'normal',label:'Normal'},{value:'loose',label:'Loose manure'},{value:'dry',label:'Dry manure'},{value:'none_observed',label:'No manure observed'},{value:'urination_concern',label:'Urination concern'}]} />
          <ChoiceRow label="Movement" value={form.movement} onChange={v => setForm(p => ({ ...p, movement: v }))}
            options={[{value:'normal',label:'Normal'},{value:'stiff',label:'Stiff'},{value:'lame',label:'Lame'},{value:'reluctant',label:'Reluctant to move'}]} />
          <ChoiceRow label="Legs / feet" value={form.legs_feet} onChange={v => setForm(p => ({ ...p, legs_feet: v }))}
            options={[{value:'normal',label:'Normal'},{value:'heat',label:'Heat'},{value:'swelling',label:'Swelling'},{value:'wound',label:'Wound'},{value:'hoof_concern',label:'Hoof concern'}]} />

          <div className="card" style={{ padding: 12, marginBottom: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Temperature (°F)</span>
              <input inputMode="decimal" value={form.temperature_f} onChange={e => setForm(p => ({ ...p, temperature_f: e.target.value }))}
                placeholder="Optional" style={{ width: 95, border: '1px solid var(--border-color)', borderRadius: 8, padding: '7px 8px', textAlign: 'right' }} />
            </label>
          </div>

          <div className="card" style={{ padding: 12, marginBottom: 8 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={3} placeholder="What did you notice?" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
          </div>

          <div className="card" style={{ padding: 12, marginBottom: 14 }}>
            <label className="form-label">Photo</label>
            <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files?.[0] || null)} />
            {photo && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>📷 {photo.name}</div>}
          </div>

          <button className="btn btn-primary btn-full" onClick={saveConcern} disabled={saving}>{saving ? 'Saving...' : 'Save Check'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: 'white', borderBottom: '1px solid var(--border-color)', padding: '12px 16px 10px', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button onClick={() => navigate('/horses')} style={{ border: 'none', background: 'none', fontSize: 26, cursor: 'pointer', padding: 0 }}>‹</button>
          <div style={{ textAlign: 'center', flex: 1, marginRight: 24 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Daily Horse Check</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Morning Check</div>
          </div>
        </div>
      </div>

      <div className="page-content" style={{ paddingBottom: 110 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="section-label" style={{ margin: 0 }}>{dateLabel(date)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{data.completed} of {data.horses.length} completed</div>
        </div>

        {data.completed > 0 && (
          <div className="card" style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div style={{ background: 'rgba(57,126,68,.10)', borderRadius: 10, padding: 9, textAlign: 'center', color: 'var(--cr-green-dark)', fontWeight: 700, fontSize: 13 }}>✓ {data.normal} Normal</div>
            <div style={{ background: data.concerns ? 'rgba(232,145,42,.12)' : 'var(--bg-secondary)', borderRadius: 10, padding: 9, textAlign: 'center', color: data.concerns ? '#C76822' : 'var(--text-muted)', fontWeight: 700, fontSize: 13 }}>⚠ {data.concerns} Concern{data.concerns === 1 ? '' : 's'}</div>
          </div>
        )}

        {!loading && unchecked.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={selectAll}>Select all</button>
            <button className="btn btn-secondary btn-sm" onClick={clear}>Clear</button>
          </div>
        )}

        {loading && <div className="loading-screen"><div className="spinner" /></div>}

        {!loading && data.horses.length === 0 && (
          <div className="empty-state"><div className="empty-state-icon">🐴</div><h3>No horses available</h3><p>You do not currently have horses available for daily checks.</p></div>
        )}

        {!loading && data.horses.map(horse => {
          const check = horse.check;
          const isSelected = selected.has(String(horse.id));
          const concern = check?.is_concern;
          return (
            <div key={horse.id} className="card" style={{ padding: '10px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, border: concern ? '1px solid rgba(232,145,42,.55)' : '1px solid var(--border-color)', background: concern ? 'rgba(255,247,236,.8)' : 'white' }}>
              {!check && horse.can_record ? (
                <input type="checkbox" checked={isSelected} onChange={() => toggleHorse(horse.id)} style={{ width: 18, height: 18, accentColor: 'var(--cr-primary)' }} />
              ) : (
                <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, background: concern ? '#E8912A' : '#4E8A48', color: 'white', flexShrink: 0 }}>{concern ? '!' : '✓'}</span>
              )}
              <Avatar horse={horse} size={38} />
              <div style={{ flex: 1, minWidth: 0 }} onClick={() => concern && openConcern(horse)}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{horse.name}</div>
                {!check && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Not checked</div>}
                {check && !concern && <div style={{ fontSize: 11, color: 'var(--cr-green-dark)' }}>Normal</div>}
                {concern && <div style={{ fontSize: 11, color: '#B85E1D' }}>{compactConcern(check)}{check.concern_status !== 'open' ? ` · ${statusLabel(check.concern_status)}` : ''}</div>}
              </div>
              {check && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{timeLabel(check.updated_at || check.created_at)}</div>}
              {concern && horse.can_record && <button onClick={() => { setFollowUpCheck(check); setFollowForm({ status: 'improving', notes: '' }); }} style={{ border: 'none', background: 'none', color: '#B85E1D', fontSize: 18, cursor: 'pointer' }}>›</button>}
            </div>
          );
        })}

        {unchecked.length > 0 && (
          <>
            <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', margin: '10px 0' }}>{unchecked.length} horse{unchecked.length === 1 ? '' : 's'} remaining</div>
            <button className="btn btn-primary btn-full" onClick={() => selected.size ? setConfirmNormal(true) : toast.error('Select at least one horse')} disabled={!selected.size}>✓ Mark Selected Normal</button>
            <button
              className="btn btn-secondary btn-full"
              onClick={() => selectedOne ? openConcern(selectedOne) : toast.error(selectedHorses.length > 1 ? 'Choose one horse to record a concern' : 'Select a horse first')}
              style={{ marginTop: 10 }}
            >
              Record Concern for Selected
            </button>
          </>
        )}

        {data.horses.length > 0 && data.completed === data.horses.length && data.concerns === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: 22, marginTop: 12 }}>
            <div style={{ fontSize: 30, color: 'var(--cr-green)' }}>✓</div>
            <div style={{ fontWeight: 700, marginTop: 4 }}>Morning check complete</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{data.horses.length} horses checked · No concerns</div>
          </div>
        )}

        {activeConcerns.length === 1 && (
          <button className="btn btn-outline btn-full" style={{ marginTop: 14 }} onClick={() => { setFollowUpCheck(activeConcerns[0].check); setFollowForm({ status: 'improving', notes: '' }); }}>Add follow-up for concern</button>
        )}
      </div>

      {confirmNormal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.48)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', width: '100%', maxWidth: 520, margin: '0 auto', borderRadius: '22px 22px 0 0', padding: 20, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ width: 50, height: 50, borderRadius: '50%', margin: '0 auto 12px', background: '#4E8A48', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>✓</div>
            <h2 style={{ textAlign: 'center', fontSize: 18, marginBottom: 4 }}>Mark {selectedHorses.length} Horse{selectedHorses.length === 1 ? '' : 's'} Normal?</h2>
            <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>This records today's morning check as normal for the selected horses.</p>
            <div className="card" style={{ padding: '4px 12px', marginBottom: 14 }}>
              {selectedHorses.map(h => <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}><Avatar horse={h} size={28}/><span style={{ fontSize: 12, fontWeight: 600 }}>{h.name}</span></div>)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 14 }}>
              <span>Observed by<br/><strong style={{ color: 'var(--text-primary)' }}>{data.actor?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ')}</strong>{data.actor?.role ? ` · ${data.actor.role}` : ''}</span>
              <span style={{ alignSelf: 'flex-end' }}>{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setConfirmNormal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={() => markNormal()}>{saving ? 'Saving...' : '✓ Mark All Normal'}</button>
            </div>
          </div>
        </div>
      )}

      {followUpCheck && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.48)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', width: '100%', maxWidth: 520, margin: '0 auto', borderRadius: '22px 22px 0 0', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div><h2 style={{ fontSize: 17, marginBottom: 2 }}>Follow-up</h2><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{compactConcern(followUpCheck)}</div></div>
              <button onClick={() => setFollowUpCheck(null)} style={{ border: 'none', background: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <label className="form-label">Current status</label>
            <select className="form-input" value={followForm.status} onChange={e => setFollowForm(p => ({ ...p, status: e.target.value }))}>
              <option value="resolved">Resolved</option>
              <option value="improving">Improving</option>
              <option value="no_change">No change</option>
              <option value="worse">Worse</option>
            </select>
            <label className="form-label" style={{ marginTop: 12 }}>Notes</label>
            <textarea className="form-input" rows={3} placeholder="What changed?" value={followForm.notes} onChange={e => setFollowForm(p => ({ ...p, notes: e.target.value }))} />
            <button className="btn btn-primary btn-full" style={{ marginTop: 14 }} disabled={saving} onClick={saveFollowUp}>{saving ? 'Saving...' : 'Save Follow-up'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyHorseCheckPage;
